// ════════════════════════════════════════════════════════════════════
// ILLEGAL EMPIRE — TheMark  (THE UNIVERSAL BEING RECORD)
// ────────────────────────────────────────────────────────────────────
// Doctrine: IE_HOLO_DOCTRINE §13. Every being carries the Empire's Mark
// whether it likes it or not. This is its barcode, its product record,
// its ingredient list — a LEDGER of every effect the being wears:
// identity, origin sigils, foil/aura tiers, adornments, migration
// history. Forced. Permanent. Krush.
//
// ── PLACEMENT (load-bearing) ─────────────────────────────────────────
// This file lives at  src/engine/TheMark.js
// Every dependency it imports lives at src/ root — theme, SourceSigil,
// HoloFoil, CosmeticManifest — so ALL FOUR imports are "./".
// Call sites import it as  "./TheMark"  (Codex, CompanionMenagerie).
//
// ── DESIGN CONTRACT ──────────────────────────────────────────────────
// • READ-ONLY. The Mark never writes. It renders whatever it's handed.
// • DATA-TOLERANT. Every section self-skips when its data is absent, so
//   the same component serves a fresh Bebecito (name only) and a
//   thrice-migrated veteran (full ledger). No section ever throws on
//   missing/partial data.
// • ONE SURFACE, EVERYWHERE. Terror is the reference implementation;
//   Bebecitos/Annihilation implement the same section order + serial +
//   Markline per THE_MARK.md so a Mark reads identically across games.
// • GM CHROME ONLY (palette law). This is UI, not board.
//
// ── THE SIGNATURE: the Markline ──────────────────────────────────────
// A deterministic barcode strip generated from the being's canonical
// key. Same being → same bars, in every game, forever. Decorative, but
// it *reads* as processing — which is the whole point.
//
// ── USAGE ────────────────────────────────────────────────────────────
//   <TheMark
//     being={{ key: "spectral_tiger_beb", name: "Spectral Tiger",
//              type: "Beb", game: "Terror" }}
//     record={collection[cardId]}         // { count, holoTiers, sources, sourceTiers }
//     equipped={profile.equipped}         // cosmetics map (optional)
//     migrations={["Bebecitos","Annihilation","Terror"]}  // optional
//     origins={["booster_shadowfox"]}     // canonical-origin fallback (optional)
//     width={360}                         // optional
//   />
//
// ── CONTRADICTION-CATCH (flagged, not resolved) ──────────────────────
// 1. HOLO tier display metadata is currently duplicated as local maps
//    in Codex.js AND CollectionViewer.js. The Mark does NOT add a third
//    copy — it derives label + color from HoloFoil's HOLO_VISUALS (the
//    recipe table) so it can never drift. The two local maps should
//    eventually consolidate into the shared registry (doctrine §4).
// 2. The cosmetic slot named `aura` (CosmeticManifest) collides
//    lexically with the doctrine's "aura tier" (holo). The Mark headers
//    the holo section "FOIL · AURA" and lists the cosmetic slot
//    verbatim under ADORNMENTS. Awaiting caP'n ruling on a rename.
// ════════════════════════════════════════════════════════════════════

import React from "react";
import { GM } from "./theme";
import SourceSigil, { getSourceLabel, primaryOrigin } from "./SourceSigil";
import { getHoloVisual } from "./HoloFoil";
import { COSMETICS } from "./CosmeticManifest";
import { getGrace, GRACE_SOURCES } from "./kingsGrace";

// ── KEYFRAMES — imperative injection, id-guarded (keyframe trap law).
// Reduced motion kills the glint entirely.
function ensureMarkKeyframes() {
  if (typeof document === "undefined") return;
  if (document.getElementById("themark-keyframes")) return;
  const el = document.createElement("style");
  el.id = "themark-keyframes";
  el.textContent = `
    @keyframes mkGlint {
      0%   { transform: translateX(-120%); }
      18%  { transform: translateX(120%); }
      100% { transform: translateX(120%); }
    }
    @media (prefers-reduced-motion: reduce) {
      .mk-glint { animation: none !important; opacity: 0 !important; }
    }
  `;
  document.head.appendChild(el);
}

// ── Deterministic PRNG off the being's key (mulberry32 over a string
// hash) — the same seed trick PackArt uses, so a being's Markline and
// serial are identical in every game, every session, forever.
function hashStr(s) {
  let h = 2166136261 >>> 0;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  return h >>> 0;
}
function mulberry32(seed) {
  let a = seed >>> 0;
  return () => {
    a |= 0; a = (a + 0x6d2b79f5) | 0;
    let t = Math.imul(a ^ (a >>> 15), 1 | a);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

// ── THE SERIAL — IE-BEING-SOURCE ─────────────────────────────────────
// TWO independent hashes, not one split in half. The first block is the
// being's identity (same everywhere, forever). The second block is its
// PRIMARY ORIGIN — proof of provenance stamped into the serial itself.
//
//   Starter-deck H-01  → IE-0125-D2WN
//   Booster-pulled H-01 → IE-0125-K7B3
//
// Same being, different papers. The Empire files by product line, not by
// individual copy — every H-01 from the same source shares a serial,
// because they ARE the same product. What it will not tolerate is a
// being with no provenance, hence MSNG. (One-origin law: the source half
// is resolved by primaryOrigin(), the same call that picks the sigil, so
// serial and sigil can never disagree.)
const SERIAL_MISSING_ORIGIN = "MSNG";

function serialBlock(namespace, value) {
  const h = hashStr(namespace + "::" + value);
  return (h >>> 0).toString(36).toUpperCase().padStart(4, "0").slice(0, 4);
}

function markSerial(key, source) {
  const beingBlock = serialBlock("mark", key);
  const sourceBlock = source ? serialBlock("origin", source) : SERIAL_MISSING_ORIGIN;
  return `IE-${beingBlock}-${sourceBlock}`;
}

// ── THE MARKLINE — the barcode. ~44 bars, deterministic widths,
// heights, and opacities. Pure SVG so it stays crisp at any size.
function Markline({ seedKey, width, height = 26 }) {
  const rand = mulberry32(hashStr("markline::" + seedKey));
  const bars = [];
  let x = 0;
  while (x < 100) {
    const w = 0.7 + rand() * 2.6;                 // bar width (viewBox units)
    const gap = 0.5 + rand() * 1.6;
    const tall = rand() > 0.28;                    // most bars full-height
    const op = 0.45 + rand() * 0.55;
    bars.push({ x, w, tall, op });
    x += w + gap;
  }
  return (
    <div style={{ position: "relative", overflow: "hidden" }}>
      <svg
        viewBox="0 0 100 10"
        preserveAspectRatio="none"
        width={width}
        height={height}
        style={{ display: "block" }}
      >
        {bars.map((b, i) => (
          <rect
            key={i}
            x={b.x}
            y={b.tall ? 0 : 2.2}
            width={b.w}
            height={b.tall ? 10 : 5.6}
            fill={GM.inkGold}
            opacity={b.op}
          />
        ))}
      </svg>
      {/* one restrained glint pass — the Empire's scanner */}
      <div
        className="mk-glint"
        style={{
          position: "absolute", inset: 0, pointerEvents: "none",
          background: `linear-gradient(105deg, transparent 40%, ${GM.inkGoldBright}33 50%, transparent 60%)`,
          animation: "mkGlint 8s ease-in-out infinite",
        }}
      />
    </div>
  );
}

// ── Foil tier display, derived from the ONE recipe table (HOLO_VISUALS
// via getHoloVisual) — never a third hand-maintained metadata map.
// Only apostrophe-bearing names need spelling help; everything else is
// key-minus-prefix, capitalized.
const TIER_SPECIAL_LABELS = { holo_aeternus: "A'eterni", holo_jynxi: "J'ynxi" };
function tierLabel(tierKey) {
  if (TIER_SPECIAL_LABELS[tierKey]) return TIER_SPECIAL_LABELS[tierKey];
  const raw = String(tierKey).replace(/^holo_/, "");
  return raw.charAt(0).toUpperCase() + raw.slice(1);
}
function tierColor(tierKey) {
  const v = getHoloVisual(tierKey);
  return (v && v.glow) || GM.inkGold;
}

// Prettify a cosmetic id for the ledger ("jynxi_jesters_hood" →
// "Jynxi Jesters Hood"). Titles come straight from the manifest string.
function prettyId(id) {
  return String(id)
    .split("_")
    .map((w) => (w ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(" ");
}
const SLOT_ORDER = ["head", "face", "body", "back", "aura"];

// ── Ledger row primitives — the nutrition-label bones: a left label, a
// dotted leader, a right-aligned value. Bureaucracy, rendered in gold.
const rowStyle = {
  display: "flex", alignItems: "baseline", gap: 8,
  fontFamily: GM.fontUI, fontSize: 11, color: GM.vellum,
  padding: "3px 0",
};
const leaderStyle = {
  flex: 1, borderBottom: `1px dotted ${GM.vellumFaint}`,
  transform: "translateY(-3px)", opacity: 0.55, minWidth: 12,
};
function LedgerRow({ label, value, labelColor, chip }) {
  return (
    <div style={rowStyle}>
      {chip}
      <span style={{ color: labelColor || GM.vellumDim, letterSpacing: 0.3 }}>{label}</span>
      <span style={leaderStyle} />
      <span style={{ fontWeight: 700, color: GM.vellum, whiteSpace: "nowrap" }}>{value}</span>
    </div>
  );
}

// Section header — heavy rule below, ALL CAPS, Cinzel. The thick-rule
// grammar is the label's skeleton: a section IS a declaration.
function SectionHead({ children }) {
  return (
    <div
      style={{
        fontFamily: GM.font, fontWeight: 900, fontSize: 11,
        letterSpacing: 3, textTransform: "uppercase", color: GM.inkGold,
        borderBottom: `2px solid ${GM.pageBorder}`,
        padding: "10px 0 4px", marginBottom: 4,
      }}
    >
      {children}
    </div>
  );
}

// ════════════════════════════════════════════════════════════════════
// THE MARK
// ════════════════════════════════════════════════════════════════════
export default function TheMark({
  being = {},          // { key, name, type, game }
  record = null,       // collection record: { count, holoCount, holoTiers, sources, sourceTiers }
  equipped = null,     // cosmetics: { head, face, body, back, aura, title }
  migrations = [],     // ["Bebecitos", "Annihilation", "Terror"]
  origins = [],        // canonical-origin fallback for primaryOrigin
  graces = [],         // King's Grace copies: [{ graceId, source }] (kingsGrace.gracesFor)
  width = 360,
  style = {},
}) {
  React.useEffect(() => { ensureMarkKeyframes(); }, []);

  const key = being.key || being.name || "unmarked";

  // ── ORIGIN data ────────────────────────────────────────────────────
  // Resolved BEFORE the serial: the serial's second block is derived
  // from the primary origin, so this must come first.
  const primary = primaryOrigin(record, origins);
  const serial = markSerial(key, primary);

  const sourceCounts = {};
  if (record?.sources) {
    for (const [s, n] of Object.entries(record.sources)) {
      if (n > 0) sourceCounts[s] = (sourceCounts[s] || 0) + n;
    }
  }
  if (record?.sourceTiers) {
    for (const m of Object.values(record.sourceTiers)) {
      for (const [s, n] of Object.entries(m || {})) {
        if (n > 0) sourceCounts[s] = (sourceCounts[s] || 0) + n;
      }
    }
  }
  const secondarySources = Object.entries(sourceCounts)
    .filter(([s]) => s !== primary)
    .sort((a, b) => b[1] - a[1]);

  // ── FOIL / AURA data ───────────────────────────────────────────────
  const tiers = Object.entries(record?.holoTiers || {}).filter(([, n]) => n > 0);

  // ── ADORNMENTS data ────────────────────────────────────────────────
  const wornSlots = SLOT_ORDER
    .map((slot) => [slot, equipped?.[slot]])
    .filter(([, id]) => !!id);
  const titleId = equipped?.title;
  const titleText = titleId ? (COSMETICS.title?.[titleId] || prettyId(titleId)) : null;

  // ── KING'S GRACE data — one row per COPY, rarest first, each with the
  // sigil of where it came from. Kept out of `sources` on purpose: a
  // bought Grace must never replace the companion's own origin line.
  const graceRows = (graces || []).filter((c) => c && getGrace(c.graceId));

  const hasAnyLedger =
    !!primary || secondarySources.length > 0 || tiers.length > 0 || graceRows.length > 0 ||
    wornSlots.length > 0 || !!titleText || migrations.length > 0;

  return (
    <div
      style={{
        width,
        // a percentage width must include the padding, or the Mark spills
        // past its panel (seen in the Menagerie, 2026-09-16). Fixed pixel
        // widths keep their old box so no existing layout moves.
        ...(typeof width === "string" && width.endsWith("%") ? { boxSizing: "border-box" } : null),
        background: `linear-gradient(180deg, ${GM.pageMid} 0%, ${GM.pageWarm} 100%)`,
        border: `1px solid ${GM.pageBorder}`,
        borderTop: `6px solid ${GM.inkGold}`,   // the label's thick top rule
        borderRadius: 10,
        padding: "14px 16px 12px",
        boxShadow: `0 8px 28px rgba(0,0,0,0.55)`,
        position: "relative",
        ...style,
      }}
    >
      {/* ── STAMP HEADER ─────────────────────────────────────────── */}
      <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between" }}>
        <div style={{ fontFamily: GM.font, fontWeight: 900, fontSize: 15, letterSpacing: 4, color: GM.inkGoldBright }}>
          THE MARK
        </div>
        <div style={{ fontFamily: GM.fontUI, fontSize: 9, letterSpacing: 1.5, color: GM.vellumFaint }}>
          {serial}
        </div>
      </div>
      <div style={{ fontFamily: GM.fontUI, fontSize: 8, letterSpacing: 2.2, color: GM.vellumFaint, textTransform: "uppercase", marginTop: 2 }}>
        Property of the Illegal Empire
      </div>

      {/* ── THE MARKLINE ─────────────────────────────────────────── */}
      <div style={{ margin: "10px 0 2px" }}>
        <Markline seedKey={key} width="100%" />
      </div>

      {/* ── IDENTITY ─────────────────────────────────────────────── */}
      <div style={{ borderBottom: `2px solid ${GM.pageBorder}`, padding: "8px 0 10px" }}>
        <div style={{ fontFamily: GM.fontD, fontSize: 22, fontWeight: 700, color: GM.vellum, lineHeight: 1.15 }}>
          {being.name || prettyId(key)}
        </div>
        {(being.type || being.game) && (
          <div style={{ fontFamily: GM.fontUI, fontSize: 10, letterSpacing: 1.2, color: GM.vellumDim, marginTop: 3, textTransform: "uppercase" }}>
            {[being.type, being.game && `Game of record · ${being.game}`].filter(Boolean).join("  ·  ")}
          </div>
        )}
      </div>

      {/* ── ORIGIN ───────────────────────────────────────────────── */}
      {(primary || secondarySources.length > 0) && (
        <div>
          <SectionHead>Origin</SectionHead>
          {primary && (
            <div style={{ display: "flex", alignItems: "center", gap: 10, padding: "4px 0 6px" }}>
              <SourceSigil source={primary} size={40} />
              <div>
                <div style={{ fontFamily: GM.fontUI, fontSize: 11, fontWeight: 700, color: GM.inkGold, letterSpacing: 0.5 }}>
                  {getSourceLabel(primary)}
                </div>
                <div style={{ fontFamily: GM.fontUI, fontSize: 8.5, letterSpacing: 1.5, color: GM.vellumFaint, textTransform: "uppercase" }}>
                  Primary origin
                </div>
              </div>
            </div>
          )}
          {secondarySources.map(([s, n]) => (
            <LedgerRow
              key={s}
              label={getSourceLabel(s)}
              value={`×${n}`}
              chip={<SourceSigil source={s} size={16} />}
            />
          ))}
        </div>
      )}

      {/* ── FOIL · AURA ──────────────────────────────────────────── */}
      {tiers.length > 0 && (
        <div>
          <SectionHead>Foil · Aura</SectionHead>
          {tiers.map(([t, n]) => (
            <LedgerRow
              key={t}
              label={tierLabel(t)}
              labelColor={tierColor(t)}
              value={`×${n}`}
              chip={
                <span
                  style={{
                    width: 9, height: 9, borderRadius: 2, flexShrink: 0,
                    background: tierColor(t),
                    boxShadow: `0 0 6px ${tierColor(t)}88`,
                    transform: "rotate(45deg)",
                  }}
                />
              }
            />
          ))}
        </div>
      )}

      {/* ── KING'S GRACE ─────────────────────────────────────────── */}
      {graceRows.length > 0 && (
        <div>
          <SectionHead>King's Grace</SectionHead>
          {graceRows.map((c) => {
            const g = getGrace(c.graceId);
            const src = GRACE_SOURCES[c.source];
            return (
              <LedgerRow
                key={`${c.graceId}~${c.source}`}
                label={`${g.label} · ${g.crown}`}
                labelColor={g.color}
                value={src ? src.short : c.source}
                chip={<SourceSigil source={src ? src.sigil : c.source} size={16} />}
              />
            );
          })}
        </div>
      )}

      {/* ── ADORNMENTS ───────────────────────────────────────────── */}
      {(wornSlots.length > 0 || titleText) && (
        <div>
          <SectionHead>Adornments</SectionHead>
          {wornSlots.map(([slot, id]) => (
            <LedgerRow key={slot} label={slot} value={prettyId(id)} />
          ))}
          {titleText && (
            <div style={{ fontFamily: GM.fontBody, fontStyle: "italic", fontSize: 13, color: GM.inkGoldBright, padding: "5px 0 2px", letterSpacing: 0.5 }}>
              “{titleText}”
            </div>
          )}
        </div>
      )}

      {/* ── MIGRATION ────────────────────────────────────────────── */}
      {migrations.length > 0 && (
        <div>
          <SectionHead>Migration</SectionHead>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, padding: "4px 0 2px" }}>
            {migrations.map((g, i) => (
              <React.Fragment key={`${g}_${i}`}>
                {i > 0 && <span style={{ color: GM.inkGoldDim, fontSize: 10 }}>➤</span>}
                <span
                  style={{
                    fontFamily: GM.fontUI, fontSize: 9.5, fontWeight: 800,
                    letterSpacing: 1.8, textTransform: "uppercase",
                    color: i === migrations.length - 1 ? GM.inkGoldBright : GM.vellumDim,
                    border: `1px solid ${GM.pageBorder}`,
                    borderRadius: 4, padding: "2px 7px",
                    background: GM.pageLight,
                  }}
                >
                  {g}
                </span>
              </React.Fragment>
            ))}
          </div>
        </div>
      )}

      {/* Fresh being, empty ledger — the Empire has simply not finished
          with it yet. An empty screen is an invitation. */}
      {!hasAnyLedger && (
        <div style={{ fontFamily: GM.fontBody, fontStyle: "italic", fontSize: 12.5, color: GM.vellumFaint, padding: "10px 0 4px" }}>
          Processing incomplete. The Empire will finish its accounting.
        </div>
      )}

      {/* ── FOOTER STAMP ─────────────────────────────────────────── */}
      <div
        style={{
          marginTop: 12, paddingTop: 7,
          borderTop: `2px solid ${GM.pageBorder}`,
          display: "flex", justifyContent: "space-between", alignItems: "baseline",
        }}
      >
        <span style={{ fontFamily: GM.fontUI, fontSize: 8, letterSpacing: 2.4, color: GM.inkRed, fontWeight: 800, textTransform: "uppercase" }}>
          The Mark is permanent
        </span>
        <span style={{ fontFamily: GM.fontUI, fontSize: 8, letterSpacing: 1.5, color: GM.vellumFaint }}>
          IE·§13
        </span>
      </div>
    </div>
  );
}
